
import React, { useMemo } from 'react';
import Select, { SingleValue } from 'react-select';

interface TimezoneOption {
    value: string;
    label: string;
    offset: number; // For sorting
}

interface TimezoneSelectProps {
    value: string;
    onChange: (value: string) => void;
    className?: string;
    placeholder?: string;
}

const TimezoneSelect: React.FC<TimezoneSelectProps> = ({
    value,
    onChange,
    className,
    placeholder = "Select timezone..."
}) => {
    // Generate timezone options with current offsets
    const options = useMemo(() => {
        // Get all supported timezones
        const timeZones = (Intl as any).supportedValuesOf('timeZone');

        const formattedOptions: TimezoneOption[] = timeZones.map(tz => {
            try {
                // Get the current offset for this timezone
                const now = new Date();
                const str = now.toLocaleString('en-US', { timeZone: tz, timeZoneName: 'longOffset' });
                // Extract offset part like "GMT-05:00"
                // Example str: "12/10/2025, 12:52:51 PM GMT+5:30"
                const offsetPart = str.split('GMT')[1] || '';
                const offsetStr = `GMT${offsetPart}`;

                // Calculate numeric offset for sorting (minutes)
                // offsetPart is like "+5:30" or "-05:00"
                let offsetMinutes = 0;
                if (offsetPart) {
                    const [hours, minutes] = offsetPart.replace('+', '').split(':').map(Number);
                    const sign = offsetPart.startsWith('-') ? -1 : 1;
                    offsetMinutes = sign * ((Math.abs(hours) * 60) + (minutes || 0));
                }

                return {
                    value: tz,
                    label: `(${offsetStr}) ${tz.replace(/_/g, ' ')}`,
                    offset: offsetMinutes
                };
            } catch (e) {
                // Fallback for weird timezone strings
                return {
                    value: tz,
                    label: tz,
                    offset: 0
                };
            }
        });

        // Sort by offset
        return formattedOptions.sort((a, b) => a.offset - b.offset);
    }, []);

    // Find the selected option object
    const selectedOption = options.find(opt => opt.value === value) || null;

    const handleChange = (newValue: SingleValue<TimezoneOption>) => {
        if (newValue) {
            onChange(newValue.value);
        }
    };

    return (
        <div className={className}>
            <Select
                value={selectedOption}
                onChange={handleChange}
                options={options}
                placeholder={placeholder}
                classNamePrefix="react-select"
                isSearchable
                styles={{
                    control: (base) => ({
                        ...base,
                        borderColor: 'hsl(var(--input))',
                        borderRadius: 'var(--radius)',
                        minHeight: '2.5rem',
                        '&:hover': {
                            borderColor: 'hsl(var(--input))',
                        },
                    }),
                    menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                    }),
                    option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? 'hsl(var(--primary))' : state.isFocused ? 'hsl(var(--accent))' : 'transparent',
                        color: state.isSelected ? 'hsl(var(--primary-foreground))' : state.isFocused ? 'hsl(var(--accent-foreground))' : 'inherit',
                    }),
                    singleValue: (base) => ({
                        ...base,
                        color: 'hsl(var(--foreground))',
                    }),
                    input: (base) => ({
                        ...base,
                        color: 'hsl(var(--foreground))',
                    }),
                }}
            />
        </div>
    );
};

export default TimezoneSelect;
